import { Injectable } from '@angular/core';
import { catchError, lastValueFrom, map, Observable, throwError } from 'rxjs';
import { ApiService } from '../../api/api.service';
import { FirstColumnListingComponent } from 'src/app/modules/shipments/components/first-column-listing/first-column-listing.component';
import { environment } from 'src/environments/environment';
import { ActionButtonsComponent } from 'src/app/modules/shipments/components/action-buttons/action-buttons.component';
import { IDateFilterParams } from 'ag-grid-community';
import { format } from 'date-fns';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { CustomBadgeRendererComponent } from 'src/app/components/custom-badge-renderer/custom-badge-renderer.component';
import { ProviderRendererComponent } from 'src/app/modules/shipments/components/provider-renderer/provider-renderer.component';
import { AttachmentBookingsComponent } from 'src/app/modules/shipments/components/attachment-bookings/attachment-bookings.component';
import { DateStatusComponent } from 'src/app/components/date-status/date-status.component';
import { StorageService } from '../../storage/storage.service';
import { saveAs } from 'file-saver';
import Swal from 'sweetalert2';

@Injectable({
  providedIn: 'root',
})
export class ProjectService {
  protected endpoint?: string;
  private domain?: string;

  constructor(
    private apiService: ApiService,
    private http: HttpClient,
  ) {
    this.endpoint = 'project';
    this.domain = `${environment.domain}/${environment.apiVersion}`;
  }

  generateInvoice(projectIds: any[] | undefined) {
    return this.apiService.post(`multi-listing/create-invoice`, {
      project_ids: projectIds,
    });
  }

  updateInvoiceStatus(payloads: any) {
    return this.apiService.put(`multi-listing/broker-update-status`, payloads);
  }

  getCoreStatusDisplay(status: string) {
    return lastValueFrom(this.apiService.get(`customer/status-display/${status}`));
  }

  updateAdjustmentStatus(payloads: any) {
    return this.apiService.post(`multi-listing/customer/adjustments/update-status`, payloads);
  }

  cancelBooking(payloads: any) {
    return this.apiService.put(`multi-listing/cancel`, payloads);
  }

  viewInvoice(invoiceId: string) {
    const endpoint = `${this.domain}/multi-listing/invoice/view/${invoiceId}`;
    window.open(endpoint, '_blank');
  }

  uploadPODFile(payloads: any) {
    return this.apiService.postProgressive(`proof-of-delivery/pending`, payloads);
  }

  deletePODFile(fileId: string) {
    return this.apiService.delete(`proof-of-delivery/pending/${fileId}`);
  }

  uploadPalletDocketFile(payloads: any) {
    return this.apiService.postProgressive(`pallet-dockets/pending`, payloads);
  }

  deletePalletDocketFile(fileId: string) {
    return this.apiService.delete(`pallet-dockets/pending/${fileId}`);
  }

  deletePalletDocket(bookingId: string | number) {
    return this.apiService.delete(`pallet-dockets/${bookingId}`);
  }

  updatePalletDocketType(bookingId: string | number, palletDocketType: string) {
    return this.apiService.patch(`pallet-dockets/${bookingId}`, { palletDocketType });
  }

  getPalletDocketDocument(bookingId: string | number) {
    return this.apiService.get('pallet-dockets/doc-attachments', { booking_id: bookingId }, false, { responseType: 'blob' });
  }

  getPodDocument(bookingId: string | number, proofType: string) {
    return this.apiService.get('listing/upload-proof/doc-attachments', { booking_id: bookingId, proof_type: proofType }, false, { responseType: 'blob' });
  }

  submitDelivery(payloads: any) {
    return this.apiService.post(`pods`, payloads);
  }

  submitPalletDocket(payloads: any) {
    return this.apiService.post(`pallet-dockets`, payloads);
  }

  fetchProjectListing() {
    return this.apiService.get(`customer/project-listing`);
  }

  fetchBookingListing(bookingId: string | number, hasLineItems: boolean = true) {
    return this.apiService.get(`customer/booking-listing`, {
      booking_id: bookingId,
      has_lineitems: hasLineItems,
    });
  }

  fetchProjectListingV2(payloads: any) {
    return this.apiService.post(`customer/project-listings-v2`, payloads);
  }
  async fetchProjectManifestDetails(payloads: any) {
    return await lastValueFrom(this.apiService.post(`customer/project-listing-details`, payloads));
  }

  fetchProjectManifestDetailsV2(payloads: any) {
    // return this.apiService.post(`customer/project-listing-details-v2`, payloads);
    return this.apiService.post(`customer/project-listing-details-ag-grid-v2`, payloads, true);
  }

  fetchProjectManifestDetailsExport(payloads: any) {
    return this.apiService.post(`customer/project-listing-details-ag-grid-v2`, payloads);
  }

  updateManifestStatus(payload: any) {
    return this.apiService.post(`customer/manifest/update-status`, payload, true);
  }

  updateInvoice(payloads: any) {
    return this.apiService.put(`multi-listing/update-invoice`, payloads);
  }

  createAdjustment(payloads: any) {
    return this.apiService.post(`multi-listing/create-adjustment`, payloads);
  }

  async fetchCustomerBaseOnType(type: String) {
    return await lastValueFrom(this.apiService.get(`customer/type/${type}`, true));
  }

  async fetchAdjustments() {
    return await lastValueFrom(this.apiService.get(`multi-listing/customer/adjustments`, true));
  }

  exportInvoice(projectIds: any): Observable<Blob> {
    let url = `${environment.domain}/${environment.apiVersion}/customer/project-listing/export/invoice`;

    return this.downloadFile(url, projectIds).pipe(
      map((res: Blob) => {
        const date = new Date();
        //make the filename format invoiceDDMMYYYY.csv example 03242025.csv
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
        const year = date.getFullYear();

        const fileName = `invoice${month}${day}${year}.csv`;
        saveAs(res, fileName);
        return res;
      }),
      catchError((err) => {
        // Handle 400 response and show SweetAlert
        if (err.status === 400 && err.error) {
          const reader = new FileReader();
          reader.onload = () => {
            try {
              const errorMsg = JSON.parse(reader.result as string).message;
              Swal.fire('Error', errorMsg || 'Export failed', 'error');
            } catch {
              Swal.fire('Error', 'Unexpected error occurred.', 'error');
            }
          };
          reader.readAsText(err.error);
        } else {
          Swal.fire('Error', 'Server error or unexpected issue occurred.', 'error');
        }

        return throwError(() => err); // Stop further processing
      }),
    );
  }

  exportBills(projectIds: any): Observable<Blob> {
    const url = `${environment.domain}/${environment.apiVersion}/customer/project-listing/export/bills`;

    return this.downloadFile(url, projectIds).pipe(
      map((res: Blob) => {
        const date = new Date();

        //make the filename format invoiceDDMMYYYY.csv example 03242025.csv
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
        const year = date.getFullYear();

        const fileName = `bill${month}${day}${year}.csv`;
        saveAs(res, fileName);
        return res;
      }),
      catchError((err) => {
        // Handle 400 response and show SweetAlert
        if (err.status === 400 && err.error) {
          const reader = new FileReader();
          reader.onload = () => {
            try {
              const errorMsg = JSON.parse(reader.result as string).message;
              Swal.fire('Error', errorMsg || 'Export failed', 'error');
            } catch {
              Swal.fire('Error', 'Unexpected error occurred.', 'error');
            }
          };
          reader.readAsText(err.error);
        } else {
          Swal.fire('Error', 'Server error or unexpected issue occurred.', 'error');
        }

        return throwError(() => err); // Stop further processing
      }),
    );
  }

  exportAdjustmentsXero(invoiceIds: number[]): Observable<Blob> {
    const url = `${environment.domain}/${environment.apiVersion}/multi-listing/customer/adjustments/xero-export`;
    const headers = new HttpHeaders({
      Authorization: StorageService.getCookie('AUTH'),
    });

    return this.http.post(url, { invoice_ids: invoiceIds }, { headers, responseType: 'blob' });
  }

  sendAdjustmentsToXero(invoiceIds: number[]): Observable<any> {
    return this.apiService.post(`multi-listing/customer/adjustments/xero-send`, { invoice_ids: invoiceIds }, true);
  }

  updatePickupDate(payload: any) {
    return this.apiService.post('customer/allocator-view/pickup', payload, true);
  }
  updateDeliveryDate(payload: any) {
    return this.apiService.post('customer/allocator-view/delivery', payload, true);
  }
  updateNotes(payload: any) {
    return this.apiService.post('customer/allocator-view/notes', payload, true);
  }

  updateTrackingStatus(payload: any) {
    return this.apiService.post('customer/allocator-view/tracking-status', payload, true);
  }

  fetchAllocatorDailyCounts(date: string) {
    return this.apiService.get('customer/allocator-view/daily-counts', { date }, true);
  }

  patchProjectListingDetailsRow(payload: { listing_id: number | string; field: string; value: boolean }) {
    return this.apiService.patch('customer/project-listing-details-row', payload, true);
  }

  private downloadFile(url: string, projectIds: any): Observable<Blob> {
    const headers = new HttpHeaders({
      Authorization: StorageService.getCookie('AUTH'),
    });

    return this.http.post(url, { project_ids: projectIds }, { headers, responseType: 'blob' });
  }

  downloadManifestFile(payload: any): void {
    const headers = new HttpHeaders({
      Authorization: StorageService.getCookie('AUTH'),
    });

    this.http
      .post(`${environment.domain}/${environment.apiVersion}/bulk-download-manifests`, payload, {
        headers,
        responseType: 'blob',
      })
      .subscribe((blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'manifest.zip'; // or any dynamic name
        a.click();
        window.URL.revokeObjectURL(url);
      });
  }

  /**
   * Download consignments for selected manifest groups
   * Validates consolidation rules on backend:
   * - Same Charge To
   * - Same Dispatch Date
   * - Same Pick Up Location
   *
   * Returns JSON data if valid, error with reasons if invalid
   * Frontend generates CSV from the returned data
   */
  downloadConsignmentsData(payload: any): Observable<any> {
    return this.apiService.post('bulk-download-consignments', payload);
  }

  generateRctiInvoices(payload: any): void {
    const headers = new HttpHeaders({
      Authorization: StorageService.getCookie('AUTH'),
    });

    this.http
      .post(`${environment.domain}/${environment.apiVersion}/get-rcti`, payload, {
        headers,
        responseType: 'blob',
      })
      .subscribe((blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        // Get current date in YYYY-MM-DD format
        const currentDate = new Date().toISOString().slice(0, 10);
        a.download = `rcti-${currentDate}.zip`;
        a.click();
        window.URL.revokeObjectURL(url);
      });
  }

  transformProjectBookingColumn(columns: any[]) {
    let transformedArray: any[] = [];

    transformedArray.push({
      colId: 0,
      width: 80,
      headerCheckboxSelection: true,
      headerCheckboxSelectionFilteredOnly: true,
      checkboxSelection: true,
      floatingFilter: false,
      cellRenderer: FirstColumnListingComponent,
      lockPosition: true,
      pinned: 'left',
      editable: false,
      filter: false,
      sortable: false,
      resizable: false,
    });

    columns.forEach((i, index) => {
      switch (i.attr_name) {
        case 'status':
          break;
        case 'status_display':
          transformedArray.push({
            colId: i.attr_name,
            field: i.attr_name,
            headerName: i.front_end_view,
            floatingFilterComponentParams: { suppressFilterButton: true },
            cellRenderer: CustomBadgeRendererComponent,
          });
          break;

        case 'listing_id':
          transformedArray.push({
            colId: i.attr_name,
            field: i.attr_name,
            headerName: 'Listing ID',
            filter: 'agTextColumnFilter',
            cellRenderer: function (params: any) {
              return `<a href="${environment.appUrl}/listing/${params.data.category_url_key}/${params.data.listing_id}" target="_blank">${params.data.listing_id}</a>`;
            },
            floatingFilterComponentParams: { suppressFilterButton: true },
          });

          transformedArray.push({
            colId: 'action',
            field: 'Action',
            width: 100,
            cellRenderer: ActionButtonsComponent,
            cellClass: [
              'd-flex',
              'align-items-center',
            ],
            floatingFilter: false,
            lockPosition: true,
            pinned: 'right',
            editable: false,
            filter: false,
            sortable: false,
            // resizable: false,
          });

          break;
        case 'category_url_key':
          break;
        case 'created_at':
          transformedArray.push({
            colId: i.attr_name,
            field: i.attr_name,
            headerName: i.front_end_view,
            floatingFilterComponentParams: { suppressFilterButton: true },
            filter: 'agDateColumnFilter',
            valueFormatter: (params: any) => formatDate(params.value),
            filterParams: filterDateParams,
          });

          break;
        case 'provider_business_name':
          transformedArray.push({
            colId: i.attr_name,
            field: i.attr_name,
            headerName: 'Providers',
            filter: 'agTextColumnFilter',
            cellRenderer: ProviderRendererComponent,
            // cellRenderer: function (params: any) {
            //   return `<a href="${environment.appUrl}/provider/profile/${params.data.provider_username}" target="_blank">${params.data.provider_username}</a>`;
            // },
            floatingFilterComponentParams: { suppressFilterButton: true },
          });
          break;
        case 'quote_value':
          transformedArray.push({
            colId: i.attr_name,
            field: i.attr_name,
            headerName: i.front_end_view,
            cellClass: 'ag-right-aligned-cell',
            filter: 'agTextColumnFilter',
            filterParams: {
              textMatcher: ({ filterText, value }: { filterText: string; value: string | number }) => {
                // Normalize values: remove commas and convert to lowercase
                const normalizedValue = value.toString().replace(/[,.]/g, '').toLowerCase();
                const normalizedFilterText = filterText.replace(/[,.]/g, '').toLowerCase();

                // Return true if the value includes the filter text
                return normalizedValue.includes(normalizedFilterText);
              },
            },
          });
          break;
        case 'POD':
          transformedArray.push({
            colId: i.attr_name,
            field: i.attr_name,
            headerName: i.front_end_view,
            filter: 'agSetColumnFilter',
            floatingFilter: true,
            cellRenderer: AttachmentBookingsComponent,
            valueGetter: (params: { data?: Record<string, boolean> }) => params.data?.['has_POD'] ?? params.data?.[i.attr_name],
            filterParams: {
              values: [
                true,
                false,
              ],
              valueFormatter: ({ value }: { value: boolean }) => (value ? 'w/ POD' : 'w/o POD'),
              suppressMiniFilter: true,
            },
            filterValueGetter: (params: { data?: Record<string, boolean> }) => Boolean(params.data?.['has_POD'] ?? params.data?.[i.attr_name]),
            floatingFilterComponentParams: { suppressFilterButton: true },
          });
          break;
        case 'distance_estimate':
          transformedArray.push({
            colId: i.attr_name,
            field: i.attr_name,
            headerName: 'Distance Estimate',
            filter: 'agTextColumnFilter',
            filterParams: {
              textMatcher: ({ filterText, value }: { filterText: string; value: string | number }) => {
                // Normalize values: remove commas and convert to lowercase
                const normalizedValue = value.toString().replace(/[,.]/g, '').toLowerCase();
                const normalizedFilterText = filterText.replace(/[,.]/g, '').toLowerCase();

                // Return true if the value includes the filter text
                return normalizedValue.includes(normalizedFilterText);
              },
            },
          });
          break;
        case 'delivery_date':
          transformedArray.push({
            colId: i.attr_name,
            field: i.attr_name,
            headerName: i.front_end_view,
            floatingFilterComponentParams: { suppressFilterButton: true },
            filter: 'agDateColumnFilter',
            cellRenderer: DateStatusComponent,
          });
          break;
        default:
          transformedArray.push({
            colId: i.attr_name,
            field: i.attr_name,
            headerName: i.front_end_view,
            filter: 'agTextColumnFilter',
            floatingFilterComponentParams: { suppressFilterButton: true },
          });
          break;
      }
    });

    return transformedArray;
  }

  generateManifest(payloads: any) {
    return this.apiService.post(`generate-manifest`, payloads);
  }

  async viewManifest(view_id: any) {
    const apiUrl = `${environment.domain}/${environment.apiVersion}/view-manifest/${view_id}`;
    window.open(apiUrl, '_blank');
  }
}

// DATA FORMATTING
function currencyFormatter(currency: any, sign: any) {
  if (!currency) {
    return sign + '0';
  }
  var sansDec = currency.toFixed(0);
  var formatted = sansDec.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return sign + `${formatted}`;
}

function formatDate(timestamp: string | number | Date) {
  if (!timestamp) return '';
  return format(new Date(timestamp), 'dd/MM/yyyy');
}

var filterDateParams: IDateFilterParams = {
  comparator: (filterLocalDateAtMidnight: Date, cellValue: string) => {
    var dateAsString = formatDate(cellValue);
    if (dateAsString == null || dateAsString == '') return -1;
    var dateParts = dateAsString.split('/');
    var cellDate = new Date(Number(dateParts[2]), Number(dateParts[1]) - 1, Number(dateParts[0]));
    if (filterLocalDateAtMidnight.getTime() === cellDate.getTime()) {
      return 0;
    }
    if (cellDate < filterLocalDateAtMidnight) {
      return -1;
    }
    if (cellDate > filterLocalDateAtMidnight) {
      return 1;
    }
    return 0;
  },
  browserDatePicker: true,
  minValidYear: 2000,
  maxValidYear: 2100,
  // inRangeFloatingFilterDateFormat: 'Do MMM YYYY',
};
